import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { addPendingDomain, selectPendingDomains, selectPermissioningCache, selectSelectedAccountId, useAppDispatch, useAppSelector, } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ResourceBehavior } from '@ibk/shared-types';
import { localStorageService, StorageKeys } from '@ibk/shared-utils';
import { ignoredRoutes } from '../../utils';
import { getInfoArray } from '../../utils/getInfoArray';
const useRequiresAuthorization = (domain, resource, featureFlag, disableRequest, resourceBehavior = ResourceBehavior.AND) => {
    const resources = getInfoArray(resource);
    const featureFlags = getInfoArray(featureFlag);
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag(undefined, { autoLoad: !ignoredRoutes.includes(pathname) });
    const permissioningCache = useAppSelector(selectPermissioningCache);
    const addPendingDomains = useAppSelector(selectPendingDomains);
    const selectedAccountId = useAppSelector(selectSelectedAccountId);
    const authToken = localStorageService.getItemSync(StorageKeys.AuthToken);
    const resourceCheckMethod = resourceBehavior === ResourceBehavior.OR ? 'some' : 'every';
    const isCurrentPermissionLoading = domain ? addPendingDomains.includes(domain) : false;
    const grabInitial = () => {
        if (!domain && !resources) {
            if (!featureFlags) {
                return true;
            }
            else {
                const isFeatureFlagApproved = featureFlags?.every(featFlag => isFeatureFlagWhiteListed(featFlag));
                return isFeatureFlagApproved;
            }
        }
        const isFeatureFlagApproved = featureFlags
            ? featureFlags?.every(featFlag => isFeatureFlagWhiteListed(featFlag))
            : true;
        if (!isFeatureFlagApproved) {
            return false;
        }
        if (permissioningCache && domain && permissioningCache[domain] && !addPendingDomains.includes(domain)) {
            return resources?.[resourceCheckMethod](res => permissioningCache[domain]?.includes(res));
        }
        return undefined;
    };
    const [show, setShow] = useState(grabInitial());
    useEffect(() => {
        if (!disableRequest && domain && !!authToken && !!selectedAccountId) {
            const alreadySearchedPermission = permissioningCache?.[domain];
            if (!alreadySearchedPermission) {
                const isFeatureFlagApproved = featureFlags
                    ? featureFlags?.every(featFlag => isFeatureFlagWhiteListed(featFlag))
                    : true;
                if (isFeatureFlagApproved) {
                    dispatch(addPendingDomain({ domain }));
                }
            }
        }
    }, [disableRequest, authToken, selectedAccountId]);
    useEffect(() => {
        if (!domain && !resources) {
            if (featureFlags) {
                const isFeatureFlagApproved = featureFlags
                    ? featureFlags?.every(featFlag => isFeatureFlagWhiteListed(featFlag))
                    : true;
                setShow(isFeatureFlagApproved);
                return;
            }
            setShow(true);
            return;
        }
        if (permissioningCache && domain && !addPendingDomains.includes(domain)) {
            const hasProperResources = resources?.[resourceCheckMethod](res => permissioningCache[domain]?.includes(res));
            setShow(!!hasProperResources);
            return;
        }
        setShow(undefined);
    }, [permissioningCache, addPendingDomains]);
    if (isCurrentPermissionLoading) {
        return undefined;
    }
    return show;
};
export default useRequiresAuthorization;
